import React, { createContext, useContext, useState, useEffect } from 'react';
import { authApi } from '../lib/api';
import { safeStorage } from '../lib/safeStorage';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const oauthToken = urlParams.get('token');
    const shop = urlParams.get('shop');
    const host = urlParams.get('host');

    // Case 1: OAuth callback with token in URL (legacy flow)
    if (oauthToken) {
      window.history.replaceState({}, document.title, window.location.pathname);
      safeStorage.setItem('token', oauthToken);
      if (shop) safeStorage.setItem('shop_domain', shop);

      authApi.getMe()
        .then((res) => {
          setUser(res.data);
          safeStorage.setItem('user', JSON.stringify(res.data));
          setIsAuthenticated(true);
        })
        .catch(() => {
          safeStorage.removeItem('token');
        })
        .finally(() => setLoading(false));
      return;
    }

    // Case 2: Shopify embedded app - authenticate via App Bridge session tokens
    // We treat the app as "embedded" if EITHER the embedded flag is set OR window.shopify
    // (App Bridge global) is present OR we are inside an iframe with a shop param in the
    // initial URL (host can be stripped on subsequent navigations).
    const inIframe = (() => { try { return window.self !== window.top; } catch (e) { return true; } })();
    const isEmbedded = !!window.__SHOPIFY_EMBEDDED__ || !!window.shopify || (inIframe && !!shop);
    if (isEmbedded) {
      if (shop) safeStorage.setItem('shop_domain', shop);

      // Retry session token auth with increasing delays (App Bridge may need time to init)
      const trySessionAuth = (attempt = 0) => {
        authApi.getMe()
          .then((res) => {
            setUser(res.data);
            safeStorage.setItem('user', JSON.stringify(res.data));
            setIsAuthenticated(true);
            setLoading(false);
          })
          .catch(() => {
            if (attempt < 3) {
              // App Bridge might not be ready yet - retry with backoff
              setTimeout(() => trySessionAuth(attempt + 1), 600 * (attempt + 1));
            } else {
              setLoading(false);
            }
          });
      };

      // Wait for App Bridge idToken to be available before first attempt
      const waitForAppBridge = (waited = 0) => {
        if (window.shopify && window.shopify.idToken) {
          trySessionAuth(0);
        } else if (waited < 3000) {
          setTimeout(() => waitForAppBridge(waited + 200), 200);
        } else {
          // App Bridge didn't init in time, try anyway (interceptor will use fallback)
          trySessionAuth(0);
        }
      };
      waitForAppBridge(0);
      return;
    }

    // Case 3: Regular session restore from storage
    const token = safeStorage.getItem('token');
    const savedUser = safeStorage.getItem('user');

    if (token && savedUser) {
      try {
        setUser(JSON.parse(savedUser));
        setIsAuthenticated(true);
      } catch {
        safeStorage.removeItem('user');
        safeStorage.removeItem('token');
        setLoading(false);
        return;
      }
      authApi.getMe()
        .then((res) => {
          setUser(res.data);
          safeStorage.setItem('user', JSON.stringify(res.data));
        })
        .catch(() => {
          logout();
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const response = await authApi.login({ email, password });
    const { access_token, user: userData } = response.data;

    safeStorage.setItem('token', access_token);
    safeStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    setIsAuthenticated(true);

    return userData;
  };

  const register = async (email, password, shop_domain) => {
    const response = await authApi.register({ email, password, shop_domain });
    const { access_token, user: userData } = response.data;

    safeStorage.setItem('token', access_token);
    safeStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    setIsAuthenticated(true);

    return userData;
  };

  const logout = () => {
    safeStorage.removeItem('token');
    safeStorage.removeItem('user');
    setUser(null);
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, isAuthenticated, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
